import { CURRENT_CONFIG } from './api.config'
import { ABORT_CODE, ABORT_ERROR_NAME } from './constants'

/**
 * Configuration options for the retry mechanism
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number
  /** Initial delay in milliseconds */
  initialDelayMs: number
  /** Maximum delay in milliseconds */
  maxDelayMs: number
  /** Jitter factor (0-1) to randomize delay */
  jitterFactor: number
  /** HTTP status codes that should trigger a retry */
  retryableStatusCodes: number[]
  /** Whether to retry on network errors */
  retryNetworkErrors: boolean
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 300,
  maxDelayMs: 5000,
  jitterFactor: 0.2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  retryNetworkErrors: false
}

/**
 * Determines if an error is retryable based on the retry configuration
 * @param error The error to check
 * @param config Retry configuration
 * @returns Whether the error is retryable
 */
export function isRetryableError(error: any, config: RetryConfig = DEFAULT_RETRY_CONFIG): boolean {
  // Don't retry aborted requests
  if (error.name === ABORT_ERROR_NAME || error.code === ABORT_CODE) {
    return false
  }

  // Retry network errors if configured
  if (
    config.retryNetworkErrors &&
    (error.message?.includes('Network Error') || error.message?.includes('timeout') || error.message?.includes('connection refused'))
  ) {
    return true
  }

  // Retry based on status code
  const statusCode = error.status || error.response?.status
  if (statusCode && config.retryableStatusCodes.indexOf(statusCode) !== -1) {
    return true
  }

  return false
}

/**
 * Calculates the delay for the next retry attempt using exponential backoff with jitter
 * @param retryAttempt Current retry attempt (0-based)
 * @param config Retry configuration
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(retryAttempt: number, config: RetryConfig = DEFAULT_RETRY_CONFIG): number {
  // Calculate exponential backoff: initialDelay * 2^attempt
  const exponentialDelay = config.initialDelayMs * Math.pow(2, retryAttempt)

  // Apply maximum delay cap
  const cappedDelay = Math.min(exponentialDelay, config.maxDelayMs)

  // Apply jitter to prevent thundering herd problem
  const jitter = config.jitterFactor * cappedDelay * (Math.random() - 0.5)

  return Math.max(0, cappedDelay + jitter)
}

/**
 * Executes a function with retry logic using exponential backoff if enabled in configuration
 * Circuit breaking happens at the module level - this function handles the enableRetry check
 * @param fn Function to execute
 * @param config Retry configuration
 * @returns Promise that resolves with the function result or rejects with the last error
 */
export async function executeWithRetry<T>(fn: () => Promise<T>, config: RetryConfig = DEFAULT_RETRY_CONFIG): Promise<T> {
  // Circuit breaking at module level - skip retry logic if disabled in configuration
  if (!CURRENT_CONFIG.enableRetry) {
    return fn()
  }

  let lastError: any

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      // First attempt (attempt=0) or retry attempts
      return await fn()
    } catch (error) {
      lastError = error

      // Check if we should retry
      if (attempt < config.maxRetries && isRetryableError(error, config)) {
        // Calculate delay with exponential backoff
        const delayMs = calculateBackoffDelay(attempt, config)

        // Log retry attempt
        console.log(`API call failed, retrying in ${delayMs}ms (attempt ${attempt + 1}/${config.maxRetries})`, error)

        // Wait before next retry
        await new Promise((resolve) => setTimeout(resolve, delayMs))

        // Continue to next iteration (retry)
        continue
      }

      // Either max retries reached or error is not retryable
      throw error
    }
  }

  // This should never be reached due to the throw in the catch block,
  // but TypeScript requires a return statement
  throw lastError
}
